import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Grid,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

// Import our custom hook
import { useImageUpload } from '../hooks/useImageUpload';

// Import constants
import { getAllowedFileTypes, getFileSizeLimit } from '../utils/constants';

/**
 * ImageUpload Component
 *
 * Provides drag-and-drop file upload functionality for survey images.
 * Integrates with React Hook Form and provides comprehensive file validation.
 *
 * Features:
 * - Drag and drop interface
 * - File validation (type, size, count)
 * - Preview generation
 * - File removal
 * - Progress indication
 * - Error handling and display
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Form field onChange handler
 * @param {Function} props.onBlur - Form field onBlur handler
 * @param {Array} props.value - Current field value (array of files)
 * @param {string} props.name - Field name
 * @param {number} props.sectionIndex - Section index for file naming
 * @param {number} props.areaIndex - Area index for file naming
 * @param {string} props.fieldName - Full field name
 * @param {Object} props.error - Validation error object
 */
const ImageUpload = ({
  onChange,
  onBlur,
  value = [],
  name,
  sectionIndex,
  areaIndex,
  fieldName,
  error,
}) => {
  // State for file previews
  const [filePreviews, setFilePreviews] = useState([]);

  // Use our custom image upload hook
  const {
    uploadStatus,
    uploadError,
    processFiles,
    generatePreviews,
    removeFile,
    cleanupPreviews,
    getFileInfo,
    canUpload,
    resetUploadState,
    getStatusMessage,
    isProcessing,
    hasError,
    maxFilesPerArea,
  } = useImageUpload();

  /**
   * Handles file drops and selections
   */
  const onDrop = async (acceptedFiles) => {
    if (!canUpload()) {
      return;
    }

    try {
      // Process the files with validation and naming
      const { processedFiles, errors } = await processFiles(
        acceptedFiles,
        sectionIndex,
        areaIndex,
        value
      );

      if (processedFiles.length > 0) {
        // Combine with existing files
        const updatedFiles = [...value, ...processedFiles];

        // Update form field (useEffect will handle preview generation)
        onChange(updatedFiles);
      }
    } catch (error) {
    }
  };

  /**
   * Removes a file from the upload list
   */
  const handleFileRemove = (fileName) => {
    const updatedFiles = removeFile(value, fileName);

    // Update form field (useEffect will handle preview generation)
    onChange(updatedFiles);
  };

  /**
   * Configure dropzone
   */
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
      },
      disabled: isProcessing || !canUpload(),
      multiple: true,
    });

  /**
   * Generate previews when value changes
   * Using a ref to prevent infinite loops
   */
  const valueRef = useRef();

  useEffect(() => {

    // Create a stable representation of the current value
    const currentValueSig =
      value
        ?.map((f) => `${f.name}:${f.size}`)
        .sort()
        .join('|') || 'empty';
    const previousValueSig = valueRef.current || 'initial';

    // Only proceed if the value actually changed
    if (currentValueSig !== previousValueSig) {
      valueRef.current = currentValueSig;

      if (value && value.length > 0) {
        generatePreviews(value).then(setFilePreviews);
      } else {
        setFilePreviews([]);
      }
    }
  }, [value]);

  /**
   * Cleanup previews on unmount only
   */
  useEffect(() => {
    return () => {
      // Cleanup all previews when component unmounts
      if (filePreviews.length > 0) {
        cleanupPreviews(filePreviews);
      }
    };
  }, []); // Empty dependency array - only runs on unmount

  // Note: Cleanup is now handled by the filePreviews useEffect above

  /**
   * Render dropzone content
   */
  const renderDropzoneContent = () => {
    if (isProcessing) {
      return (
        <Box textAlign='center' py={4}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant='body2' color='textSecondary'>
            {getStatusMessage()}
          </Typography>
        </Box>
      );
    }

    if (isDragActive) {
      return (
        <Box textAlign='center' py={4}>
          <CloudUploadIcon
            sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
          />
          <Typography variant='h6' color='primary'>
            Drop files here
          </Typography>
        </Box>
      );
    }

    if (isDragReject) {
      return (
        <Box textAlign='center' py={4}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
          <Typography variant='h6' color='error'>
            Invalid file type
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            Please use {getAllowedFileTypes()} files only
          </Typography>
        </Box>
      );
    }

    return (
      <Box textAlign='center' py={4}>
        <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
        <Typography variant='h6' gutterBottom>
          Browse Files
        </Typography>
        <Typography variant='body2' color='textSecondary' gutterBottom>
          Drag and drop files here, or click to browse
        </Typography>
        <Typography variant='caption' color='textSecondary'>
          Max {maxFilesPerArea} files, up to {getFileSizeLimit()} each
        </Typography>
        <br />
        <Typography variant='caption' color='textSecondary'>
          Supported formats: {getAllowedFileTypes()}
        </Typography>
      </Box>
    );
  };

  /**
   * Render uploaded files list
   */
  const renderFileList = () => {
    if (filePreviews.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant='subtitle2' gutterBottom>
          Uploaded Files ({filePreviews.length})
        </Typography>

        <Grid container spacing={1}>
          {filePreviews.map((preview, index) => {
            const fileInfo = getFileInfo(preview.file);

            return (
              <Grid item xs={12} key={preview.file.name}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {/* File icon or preview */}
                  <Box sx={{ mr: 2 }}>
                    {preview.previewUrl ? (
                      <img
                        src={preview.previewUrl}
                        alt={preview.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <ImageIcon
                        sx={{ fontSize: 40, color: 'action.active' }}
                      />
                    )}
                  </Box>

                  {/* File info */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant='body2' noWrap>
                      {fileInfo.name}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {fileInfo.size} • {fileInfo.type}
                    </Typography>
                  </Box>

                  {/* Remove button */}
                  <IconButton
                    size='small'
                    onClick={() => handleFileRemove(preview.file.name)}
                    color='error'
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Dropzone */}
      <Box
        {...getRootProps()}
        sx={{
          border: 2,
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderStyle: 'dashed',
          borderRadius: 2,
          backgroundColor: isDragActive ? 'action.hover' : 'background.default',
          cursor: canUpload() ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: canUpload() ? 'primary.main' : 'divider',
            backgroundColor: canUpload()
              ? 'action.hover'
              : 'background.default',
          },
        }}
      >
        <input {...getInputProps()} />
        {renderDropzoneContent()}
      </Box>

      {/* Error Display */}
      {(hasError || uploadError || error) && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {uploadError ||
            error?.message ||
            'An error occurred with file upload'}
        </Alert>
      )}

      {/* File List */}
      {renderFileList()}

      {/* Status Message */}
      {uploadStatus !== 'idle' && !hasError && (
        <Typography
          variant='caption'
          color='success.main'
          sx={{ mt: 1, display: 'block' }}
        >
          {getStatusMessage()}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload;
